/* ══════════════════════════════════════════════════════════════
   Morning Brief Component — Full AI-Generated Brief Display
   For Intelligence page Morning Brief tab
   ══════════════════════════════════════════════════════════════ */

import { fetchLLMMorningBrief } from '../services/llm-api';
import { renderUnavailableBlock } from './data-state';

export interface MorningBriefData {
  date: string;
  marketOutlook: string;
  summary: string;
  keyPoints: string[];
  sectorOutlook: Array<{ sector: string; sentiment: string; reason: string }>;
  eventsToday: string[];
  globalCues: string;
  fiiDiiTakeaway: string;
  riskFactors: string[];
}

function mapBriefResponse(brief: {
  summary?: string;
  highlights?: string[];
  sentiment?: string;
  generatedAt?: string;
}): MorningBriefData {
  const sentiment = brief.sentiment === 'bullish' || brief.sentiment === 'bearish' ? brief.sentiment : 'neutral';
  return {
    date: brief.generatedAt
      ? new Date(brief.generatedAt).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
    marketOutlook: sentiment,
    summary: brief.summary || 'Live morning brief unavailable',
    keyPoints: brief.highlights || [],
    sectorOutlook: [],
    eventsToday: [],
    globalCues: '',
    fiiDiiTakeaway: '',
    riskFactors: [],
  };
}

export function renderMorningBrief(container: HTMLElement, data?: MorningBriefData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div style="padding: 2rem; text-align: center;">Loading AI Morning Brief...</div>`;
    fetchLLMMorningBrief().then(brief => {
      if (brief) {
        renderWithData(container, mapBriefResponse(brief));
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card', 'Live morning brief unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card', 'Live morning brief unavailable');
    });
  }
}

function renderWithData(container: HTMLElement, d: MorningBriefData): void {

  const outlookGradient = d.marketOutlook === 'bullish'
    ? 'linear-gradient(135deg, #059669, #10b981)'
    : d.marketOutlook === 'bearish'
      ? 'linear-gradient(135deg, #dc2626, #ef4444)'
      : 'linear-gradient(135deg, #d97706, #f59e0b)';

  const sentimentColors: Record<string, string> = {
    'Strong Buy': '#059669', 'Buy': '#10b981', 'Neutral': '#d97706',
    'Cautious': '#ea580c', 'Sell': '#dc2626', 'Avoid': '#991b1b',
  };

  container.innerHTML = `
    <div class="morning-brief-full">
      <div class="brief-hero" style="background: ${outlookGradient}">
        <div class="brief-hero-content">
          <span class="brief-emoji">${d.marketOutlook === 'bullish' ? '🐂' : d.marketOutlook === 'bearish' ? '🐻' : '➡️'}</span>
          <h2>Morning Market Brief</h2>
          <p class="brief-date">${d.date}</p>
          <p class="brief-hero-summary">${d.summary}</p>
        </div>
      </div>

      <div class="brief-body">
        <div class="brief-section glass-card">
          <h3>🎯 Key Points</h3>
          <ul class="key-points-list">
            ${d.keyPoints.length > 0 ? d.keyPoints.map(p => `<li>${p}</li>`).join('') : '<li>No live highlights available</li>'}
          </ul>
        </div>

        ${d.sectorOutlook.length > 0 ? `<div class="brief-section glass-card">
          <h3>📊 Sector Outlook</h3>
          <div class="sector-table">
            <div class="sector-table-header">
              <span>Sector</span><span>Sentiment</span><span>Reason</span>
            </div>
            ${d.sectorOutlook.map(s => `
              <div class="sector-table-row">
                <span class="sector-name">${s.sector}</span>
                <span class="sector-sentiment" style="color:${sentimentColors[s.sentiment] || '#888'}">${s.sentiment}</span>
                <span class="sector-reason">${s.reason}</span>
              </div>`).join('')}
          </div>
        </div>` : ''}

        ${(d.eventsToday.length > 0 || d.riskFactors.length > 0) ? `<div class="brief-two-col">
          <div class="brief-section glass-card">
            <h3>📅 Events Today</h3>
            <ul class="events-list">${d.eventsToday.map(e => `<li>${e}</li>`).join('')}</ul>
          </div>
          <div class="brief-section glass-card">
            <h3>⚠️ Risk Factors</h3>
            <ul class="risk-list">${d.riskFactors.map(r => `<li>⚡ ${r}</li>`).join('')}</ul>
          </div>
        </div>` : ''}

        ${d.globalCues ? `<div class="brief-section glass-card">
          <h3>🌍 Global Cues</h3>
          <p>${d.globalCues}</p>
        </div>` : ''}

        ${d.fiiDiiTakeaway ? `<div class="brief-section glass-card">
          <h3>🏦 FII/DII Takeaway</h3>
          <p>${d.fiiDiiTakeaway}</p>
        </div>` : ''}
        </div>
      </div>
    </div>`;
}
